import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from './firebase';

export interface NewsPaperDoc {
  id: string;
  title: string;
  abstract: string;
  authors: string[];
  source: string;
  doi: string | null;
  url: string;
  publishedAt: string;
  researchArea: string;
  citationCount: number;
  isOpenAccess: boolean;
  score: number;
  resonateCount: number;
  likeCount: number;
  viewCount: number;
  commentCount: number;
}

export async function loadNewsPapers(opts?: {
  maxCount?: number;
  researchArea?: string;
}): Promise<NewsPaperDoc[]> {
  if (!db) return [];
  try {
    const col = collection(db, 'newsPapers');
    const constraints = opts?.researchArea
      ? [where('researchArea', '==', opts.researchArea), orderBy('score', 'desc'), limit(opts?.maxCount ?? 1000)]
      : [orderBy('score', 'desc'), limit(opts?.maxCount ?? 1000)];
    const snap = await getDocs(query(col, ...constraints));
    return snap.docs.map(d => ({
      id: d.id,
      ...(d.data() as Omit<NewsPaperDoc, 'id'>),
    }));
  } catch {
    return [];
  }
}

export async function loadRecentPapers(days = 7): Promise<NewsPaperDoc[]> {
  if (!db) return [];
  try {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    const cutoffStr = cutoff.toISOString().split('T')[0];
    const col = collection(db, 'newsPapers');
    const snap = await getDocs(
      query(col, where('publishedAt', '>=', cutoffStr), orderBy('publishedAt', 'desc'), limit(500))
    );
    const papers = snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<NewsPaperDoc, 'id'>) }));
    return papers.sort((a, b) => b.score - a.score);
  } catch {
    return [];
  }
}

export function searchPapers(papers: NewsPaperDoc[], term: string): NewsPaperDoc[] {
  const q = term.toLowerCase().trim();
  if (!q) return papers;
  return papers.filter(p =>
    p.title.toLowerCase().includes(q) ||
    p.authors.some(a => a.toLowerCase().includes(q)) ||
    p.abstract.toLowerCase().includes(q) ||
    p.researchArea.toLowerCase().includes(q)
  );
}
